
    <template>
      <section class="content element-doc">
        <h2>Dialog 模态框</h2>
<p>在保留当前页面状态的情况下，告知用户并承载相关操作。</p>
<h3>基本用法</h3>
<p>Dialog 弹出一个模态框，适合需要定制性更大的场景。</p>
<demo-block>
        <div><p>需要设置<code>visible</code>属性，它接收<code>Boolean</code>，当为<code>true</code>时显示 Dialog。Dialog 分为两个部分：<code>body</code>和<code>footer</code>，<code>footer</code>需要具名为<code>footer</code>的<code>slot</code>。<code>title</code>属性用于定义标题，它是可选的，默认值为空。最后，本例还展示了<code>before-close</code>的用法。</p>
</div>
        <template slot="source"><element-demo0 /></template>
        <template slot="highlight"><pre v-pre><code class="html">&lt;el-button type=&quot;text&quot; @click=&quot;dialogVisible = true&quot;&gt;点击打开 Dialog&lt;/el-button&gt;

&lt;apex-dialog
  title=&quot;提示&quot;
  :visible.sync=&quot;dialogVisible&quot;
  width=&quot;30%&quot;
  fullscreen
  :before-close=&quot;handleClose&quot;&gt;
  &lt;span&gt;这是一段信息&lt;/span&gt;
&lt;/apex-dialog&gt;

&lt;script&gt;
  export default {
    data() {
      return {
        dialogVisible: false
      };
    },
    methods: {
      handleClose(done) {
        this.$confirm('确认关闭？')
          .then(_ =&gt; {
            done();
          })
          .catch(_ =&gt; {});
      }
    }
  };
&lt;/script&gt;
</code></pre></template></demo-block><div class="tip">
<p><code>before-close</code> 仅当用户通过点击关闭图标或遮罩关闭 Dialog 时起效。如果你在 <code>footer</code> 具名 slot 里添加了用于关闭 Dialog 的按钮，那么可以在按钮的点击回调函数里加入 <code>before-close</code> 的相关逻辑。</p>
</div>
<h3>头部标题使用插槽</h3>
<p>Dialog 标题区的内容自定义头部标题部分</p>
<demo-block>
        <div><p>设置<code>slot</code>为<code>title</code>的插槽来自定义头部标题部分，示例如下：</p>
</div>
        <template slot="source"><element-demo1 /></template>
        <template slot="highlight"><pre v-pre><code class="html">&lt;el-button type=&quot;text&quot; @click=&quot;dialogVisible = true&quot;&gt;点击打开 Dialog&lt;/el-button&gt;

&lt;apex-dialog
  :visible.sync=&quot;dialogVisible&quot;
  width=&quot;70%&quot;
  @cancel=&quot;cancel&quot;
&gt;
  &lt;template v-slot:title&gt;
    我是从插槽设置的标题
    &lt;el-button type=&quot;warning&quot; size=&quot;mini&quot;&gt;头部操作的按钮&lt;/el-button&gt;
  &lt;/template&gt;
  &lt;span&gt;自定义底部按钮&lt;/span&gt;
&lt;/apex-dialog&gt;

&lt;script&gt;
  export default {
    data() {
      return {
        dialogVisible: false,
      }
    },
    methods: {
      cancel() {
        this.dialogVisible = false
      }
    }
  };
&lt;/script&gt;
</code></pre></template></demo-block><h3>自定义底部按钮</h3>
<p>自定义底部按钮，底部按钮可居中可不居中</p>
<demo-block>
        <div><p>设置<code>btnList</code>来自定义弹框的底部按钮，设置<code>btnCenter</code>来设置底部按钮是否居中，默认为<code>true</code>居中显示，示例如下：</p>
</div>
        <template slot="source"><element-demo2 /></template>
        <template slot="highlight"><pre v-pre><code class="html">&lt;el-button type=&quot;text&quot; @click=&quot;dialogVisible = true&quot;&gt;点击打开 Dialog&lt;/el-button&gt;

&lt;apex-dialog
  title=&quot;提示&quot;
  :visible.sync=&quot;dialogVisible&quot;
  :btn-list=&quot;btnList&quot;
  :drag=&quot;false&quot;
  width=&quot;30%&quot;
  @confirm=&quot;confirm&quot;
  @cancel=&quot;cancel&quot;
&gt;
  &lt;span&gt;自定义底部按钮&lt;/span&gt;
&lt;/apex-dialog&gt;

&lt;script&gt;
  export default {
    data() {
      return {
        dialogVisible: false,
        btnList: [
          { text: '自定义按钮1', type: 'primary', emit: 'confirm' },
          { text: '自定义按钮2', type: 'default', emit: 'cancel' }
        ]
      }
    },
    methods: {
      confirm() {
        this.$message('点击了自定义按钮1')
      },
      cancel() {
        this.dialogVisible = false
      }
    }
  };
&lt;/script&gt;
</code></pre></template></demo-block><h3>全屏</h3>
<p>是否显示全屏icon按钮</p>
<demo-block>
        <div><p>需要设置<code>showFullscreen</code>属性，它接收<code>Boolean</code>，当为<code>false</code>时不显示全屏icon按钮，默认为<code>true</code>显示全屏icon按钮，回调事件为toggleFullscreen。</p>
</div>
        <template slot="source"><element-demo3 /></template>
        <template slot="highlight"><pre v-pre><code class="html">&lt;el-button type=&quot;text&quot; @click=&quot;dialogVisible = true&quot;&gt;点击打开 Dialog&lt;/el-button&gt;

&lt;apex-dialog
  :show-fullscreen=&quot;true&quot;
  title=&quot;提示&quot;
  :visible.sync=&quot;dialogVisible&quot;
  width=&quot;30%&quot;
  @toggleFullscreen=&quot;toggleFullscreenFn&quot;&gt;
  &lt;span&gt;这是一段信息&lt;/span&gt;
&lt;/apex-dialog&gt;

&lt;script&gt;
  export default {
    data() {
      return {
        dialogVisible: false
      };
    },
    methods: {
      toggleFullscreenFn(isFullscreen) {
        console.log(isFullscreen)
      }
    }
  };
&lt;/script&gt;
</code></pre></template></demo-block><h3>开启拖拽 Dialog</h3>
<p>可拖拽模态框</p>
<demo-block>
        <div><p>需要设置<code>drag</code>属性，它接收<code>Boolean</code>，当为<code>true</code>时可拖拽 Dialog，默认为<code>true</code>可拖拽。</p>
</div>
        <template slot="source"><element-demo4 /></template>
        <template slot="highlight"><pre v-pre><code class="html">&lt;el-button type=&quot;text&quot; @click=&quot;dialogVisible = true&quot;&gt;点击打开 Dialog&lt;/el-button&gt;

&lt;apex-dialog drag :visible.sync=&quot;dialogVisible&quot; width=&quot;30%&quot;&gt;
  &lt;span&gt;这是一段信息&lt;/span&gt;
&lt;/apex-dialog&gt;

&lt;script&gt;
  export default {
    data() {
      return {
        dialogVisible: false
      };
    }
  };
&lt;/script&gt;
</code></pre></template></demo-block><h3>嵌入外链内容</h3>
<p>Iframe交互</p>
<demo-block>
        <div><p>传入<code>url</code>链接即可嵌入外链，它接收<code>String</code>类型。</p>
</div>
        <template slot="source"><element-demo5 /></template>
        <template slot="highlight"><pre v-pre><code class="html">&lt;el-button type=&quot;text&quot; @click=&quot;dialogVisible = true&quot;&gt;点击打开 Dialog&lt;/el-button&gt;

&lt;apex-dialog :visible.sync=&quot;dialogVisible&quot; :url=&quot;url&quot; title=&quot;外链嵌入&quot; size=&quot;medium&quot; width=&quot;70%&quot; center /&gt;

&lt;script&gt;
  export default {
    data() {
      return {
        dialogVisible: false,
        url: 'https://www.hao123.com/'
      };
    }
  };
&lt;/script&gt;
</code></pre></template></demo-block><h3>自定义内容</h3>
<p>Dialog 组件的内容可以是任意的，甚至可以是表格或表单，下面是应用了 Element Table 和 Form 组件的两个样例。</p>
<demo-block>
        
        <template slot="source"><element-demo6 /></template>
        <template slot="highlight"><pre v-pre><code class="html">&lt;!-- Table --&gt;
&lt;el-button type=&quot;text&quot; @click=&quot;dialogTableVisible = true&quot;&gt;打开嵌套表格的 Dialog&lt;/el-button&gt;

&lt;apex-dialog title=&quot;收货地址&quot; :visible.sync=&quot;dialogTableVisible&quot;&gt;
  &lt;el-table :data=&quot;gridData&quot;&gt;
    &lt;el-table-column property=&quot;date&quot; label=&quot;日期&quot; width=&quot;150&quot;&gt;&lt;/el-table-column&gt;
    &lt;el-table-column property=&quot;name&quot; label=&quot;姓名&quot; width=&quot;200&quot;&gt;&lt;/el-table-column&gt;
    &lt;el-table-column property=&quot;address&quot; label=&quot;地址&quot;&gt;&lt;/el-table-column&gt;
  &lt;/el-table&gt;
&lt;/apex-dialog&gt;

&lt;!-- Form --&gt;
&lt;el-button type=&quot;text&quot; @click=&quot;dialogFormVisible = true&quot;&gt;打开嵌套表单的 Dialog&lt;/el-button&gt;

&lt;apex-dialog title=&quot;收货地址&quot; :visible.sync=&quot;dialogFormVisible&quot;&gt;
  &lt;el-form :model=&quot;form&quot;&gt;
    &lt;el-form-item label=&quot;活动名称&quot; :label-width=&quot;formLabelWidth&quot;&gt;
      &lt;el-input v-model=&quot;form.name&quot; autocomplete=&quot;off&quot;&gt;&lt;/el-input&gt;
    &lt;/el-form-item&gt;
    &lt;el-form-item label=&quot;活动区域&quot; :label-width=&quot;formLabelWidth&quot;&gt;
      &lt;el-select v-model=&quot;form.region&quot; placeholder=&quot;请选择活动区域&quot;&gt;
        &lt;el-option label=&quot;区域一&quot; value=&quot;shanghai&quot;&gt;&lt;/el-option&gt;
        &lt;el-option label=&quot;区域二&quot; value=&quot;beijing&quot;&gt;&lt;/el-option&gt;
      &lt;/el-select&gt;
    &lt;/el-form-item&gt;
  &lt;/el-form&gt;
&lt;/apex-dialog&gt;

&lt;script&gt;
  export default {
    data() {
      return {
        gridData: [{
          date: '2016-05-02',
          name: '王小虎',
          address: '上海市普陀区金沙江路 1518 弄'
        }, {
          date: '2016-05-04',
          name: '王小虎',
          address: '上海市普陀区金沙江路 1518 弄'
        }, {
          date: '2016-05-01',
          name: '王小虎',
          address: '上海市普陀区金沙江路 1518 弄'
        }, {
          date: '2016-05-03',
          name: '王小虎',
          address: '上海市普陀区金沙江路 1518 弄'
        }],
        dialogTableVisible: false,
        dialogFormVisible: false,
        form: {
          name: '',
          region: '',
          date1: '',
          date2: '',
          delivery: false,
          type: [],
          resource: '',
          desc: ''
        },
        formLabelWidth: '120px'
      };
    }
  };
&lt;/script&gt;
</code></pre></template></demo-block><h3>嵌套的 Dialog</h3>
<p>如果需要在一个 Dialog 内部嵌套另一个 Dialog，需要使用 <code>append-to-body</code> 属性。</p>
<demo-block>
        <div><p>正常情况下，我们不建议使用嵌套的 Dialog，如果需要在页面上同时显示多个 Dialog，可以将它们平级放置。对于确实需要嵌套 Dialog 的场景，我们提供了<code>append-to-body</code>属性。将内层 Dialog 的该属性设置为 true，它就会插入至 body 元素上，从而保证内外层 Dialog 和遮罩层级关系的正确。</p>
</div>
        <template slot="source"><element-demo7 /></template>
        <template slot="highlight"><pre v-pre><code class="html">&lt;template&gt;
  &lt;el-button type=&quot;text&quot; @click=&quot;outerVisible = true&quot;&gt;点击打开外层 Dialog&lt;/el-button&gt;
  
  &lt;apex-dialog title=&quot;外层 Dialog&quot; :show-footer=&quot;false&quot; :visible.sync=&quot;outerVisible&quot;&gt;
    &lt;apex-dialog
      width=&quot;30%&quot;
      title=&quot;内层 Dialog&quot;
      :visible.sync=&quot;innerVisible&quot;
      append-to-body&gt;
    &lt;/apex-dialog&gt;
    &lt;div class=&quot;dialog-footer&quot;&gt;
      &lt;el-button @click=&quot;outerVisible = false&quot;&gt;取 消&lt;/el-button&gt;
      &lt;el-button type=&quot;primary&quot; @click=&quot;innerVisible = true&quot;&gt;打开内层 Dialog&lt;/el-button&gt;
    &lt;/div&gt;
  &lt;/apex-dialog&gt;
&lt;/template&gt;

&lt;script&gt;
  export default {
    data() {
      return {
        outerVisible: false,
        innerVisible: false
      };
    }
  }
&lt;/script&gt;
</code></pre></template></demo-block><h3>居中布局</h3>
<p>标题和底部可水平居中</p>
<demo-block>
        <div><p>将<code>center</code>设置为<code>true</code>即可使标题和底部居中。<code>center</code>仅影响标题和底部区域。Dialog 的内容是任意的，在一些情况下，内容并不适合居中布局。如果需要内容也水平居中，请自行为其添加 CSS。</p>
</div>
        <template slot="source"><element-demo8 /></template>
        <template slot="highlight"><pre v-pre><code class="html">&lt;el-button type=&quot;text&quot; @click=&quot;centerDialogVisible = true&quot;&gt;点击打开 Dialog&lt;/el-button&gt;

&lt;apex-dialog
  title=&quot;提示&quot;
  :visible.sync=&quot;centerDialogVisible&quot;
  width=&quot;30%&quot;
  center&gt;
  &lt;span&gt;需要注意的是内容是默认不居中的&lt;/span&gt;
&lt;/apex-dialog&gt;

&lt;script&gt;
  export default {
    data() {
      return {
        centerDialogVisible: false
      };
    }
  };
&lt;/script&gt;
</code></pre></template></demo-block><div class="tip">
<p>Dialog 的内容是懒渲染的，即在第一次被打开之前，传入的默认 slot 不会被渲染到 DOM 上。因此，如果需要执行 DOM 操作，或通过 <code>ref</code> 获取相应组件，请在 <code>open</code> 事件回调中进行。</p>
</div>
<div class="tip">
<p>如果 <code>visible</code> 属性绑定的变量位于 Vuex 的 store 内，那么 <code>.sync</code> 不会正常工作。此时需要去除 <code>.sync</code> 修饰符，同时监听 Dialog 的 <code>open</code> 和 <code>close</code> 事件，在事件回调中执行 Vuex 中对应的 mutation 更新 <code>visible</code> 属性绑定的变量的值。</p>
</div>
<h3>Attributes</h3>
<table>
<thead>
<tr>
<th>参数</th>
<th>说明</th>
<th>类型</th>
<th>可选值</th>
<th>默认值</th>
</tr>
</thead>
<tbody>
<tr>
<td>visible</td>
<td>是否显示 Dialog，支持 .sync 修饰符</td>
<td>boolean</td>
<td>—</td>
<td>false</td>
</tr>
<tr>
<td>title</td>
<td>Dialog 的标题，也可通过具名 slot （见下表）传入</td>
<td>string</td>
<td>—</td>
<td>—</td>
</tr>
<tr>
<td>btnLoading</td>
<td>确认按钮的loading状态</td>
<td>boolean</td>
<td>true/false</td>
<td>false</td>
</tr>
<tr>
<td>width</td>
<td>Dialog 的宽度</td>
<td>string</td>
<td>—</td>
<td>50%</td>
</tr>
<tr>
<td>confirmText</td>
<td>Dialog底部按钮confirm按钮文字</td>
<td>string</td>
<td>—</td>
<td>确定</td>
</tr>
<tr>
<td>cancleText</td>
<td>Dialog底部按钮cancle按钮文字</td>
<td>string</td>
<td>—</td>
<td>取消</td>
</tr>
<tr>
<td>showFooter</td>
<td>是否显示底部按钮</td>
<td>boolean</td>
<td>—</td>
<td>true</td>
</tr>
<tr>
<td>drag</td>
<td>是否开启拖拽功能</td>
<td>boolean</td>
<td>—</td>
<td>true</td>
</tr>
<tr>
<td>size</td>
<td>底部按钮大小</td>
<td>string</td>
<td>mini/small/medium/default</td>
<td>small</td>
</tr>
<tr>
<td>showFullscreen</td>
<td>是否显示全屏icon</td>
<td>boolean</td>
<td>—</td>
<td>true</td>
</tr>
<tr>
<td>fullscreen</td>
<td>是否为全屏 Dialog</td>
<td>boolean</td>
<td>—</td>
<td>false</td>
</tr>
<tr>
<td>top</td>
<td>Dialog CSS 中的 margin-top 值</td>
<td>string</td>
<td>—</td>
<td>15vh</td>
</tr>
<tr>
<td>modal</td>
<td>是否需要遮罩层</td>
<td>boolean</td>
<td>—</td>
<td>true</td>
</tr>
<tr>
<td>modal-append-to-body</td>
<td>遮罩层是否插入至 body 元素上，若为 false，则遮罩层会插入至 Dialog 的父元素上</td>
<td>boolean</td>
<td>—</td>
<td>true</td>
</tr>
<tr>
<td>append-to-body</td>
<td>Dialog 自身是否插入至 body 元素上。嵌套的 Dialog 必须指定该属性并赋值为 true</td>
<td>boolean</td>
<td>—</td>
<td>false</td>
</tr>
<tr>
<td>lock-scroll</td>
<td>是否在 Dialog 出现时将 body 滚动锁定</td>
<td>boolean</td>
<td>—</td>
<td>true</td>
</tr>
<tr>
<td>custom-class</td>
<td>Dialog 的自定义类名</td>
<td>string</td>
<td>—</td>
<td>—</td>
</tr>
<tr>
<td>close-on-click-modal</td>
<td>是否可以通过点击 modal 关闭 Dialog</td>
<td>boolean</td>
<td>—</td>
<td>true</td>
</tr>
<tr>
<td>close-on-press-escape</td>
<td>是否可以通过按下 ESC 关闭 Dialog</td>
<td>boolean</td>
<td>—</td>
<td>true</td>
</tr>
<tr>
<td>show-close</td>
<td>是否显示关闭按钮</td>
<td>boolean</td>
<td>—</td>
<td>true</td>
</tr>
<tr>
<td>before-close</td>
<td>关闭前的回调，会暂停 Dialog 的关闭</td>
<td>function(done)，done 用于关闭 Dialog</td>
<td>—</td>
<td>—</td>
</tr>
<tr>
<td>center</td>
<td>是否对头部和底部采用居中布局</td>
<td>boolean</td>
<td>—</td>
<td>false</td>
</tr>
<tr>
<td>destroy-on-close</td>
<td>关闭时销毁 Dialog 中的元素</td>
<td>boolean</td>
<td>—</td>
<td>false</td>
</tr>
</tbody>
</table>
<h3>Slot</h3>
<table>
<thead>
<tr>
<th>name</th>
<th>说明</th>
</tr>
</thead>
<tbody>
<tr>
<td>—</td>
<td>Dialog 的内容</td>
</tr>
<tr>
<td>title</td>
<td>Dialog 标题区的内容</td>
</tr>
<tr>
<td>footer</td>
<td>Dialog 按钮操作区的内容</td>
</tr>
</tbody>
</table>
<h3>Events</h3>
<table>
<thead>
<tr>
<th>事件名称</th>
<th>说明</th>
<th>回调参数</th>
</tr>
</thead>
<tbody>
<tr>
<td>open</td>
<td>Dialog 打开的回调</td>
<td>—</td>
</tr>
<tr>
<td>opened</td>
<td>Dialog 打开动画结束时的回调</td>
<td>—</td>
</tr>
<tr>
<td>close</td>
<td>Dialog 关闭的回调</td>
<td>—</td>
</tr>
<tr>
<td>closed</td>
<td>Dialog 关闭动画结束时的回调</td>
<td>—</td>
</tr>
<tr>
<td>confirm</td>
<td>底部确定按钮的回调</td>
<td>—</td>
</tr>
<tr>
<td>cancle</td>
<td>底部取消按钮的回调</td>
<td>—</td>
</tr>
<tr>
<td>toggleFullscreenFn</td>
<td>切换全屏的回调事件</td>
<td>Boolean</td>
</tr>
</tbody>
</table>

      </section>
    </template>
    <script>
      export default {
        name: 'component-doc',
        components: {
          "element-demo0": (function() {
    
    var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',[_c('el-button',{attrs:{"type":"text"},on:{"click":function($event){_vm.dialogVisible = true}}},[_vm._v("点击打开 Dialog")]),_vm._v(" "),_c('apex-dialog',{attrs:{"title":"提示","visible":_vm.dialogVisible,"width":"30%","fullscreen":"","before-close":_vm.handleClose},on:{"update:visible":function($event){_vm.dialogVisible=$event}}},[_c('span',[_vm._v("这是一段信息")])])],1)}
var staticRenderFns = []

  
    const democomponentExport = {
    data() {
      return {
        dialogVisible: false
      };
    },
    methods: {
      handleClose(done) {
        this.$confirm('确认关闭？')
          .then(_ => {
            done();
          })
          .catch(_ => {});
      }
    }
  };
    return {
      render,
      staticRenderFns,
      ...democomponentExport
    }
  })(),"element-demo1": (function() {
    
    var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',[_c('el-button',{attrs:{"type":"text"},on:{"click":function($event){_vm.dialogVisible = true}}},[_vm._v("点击打开 Dialog")]),_vm._v(" "),_c('apex-dialog',{attrs:{"visible":_vm.dialogVisible,"width":"70%"},on:{"update:visible":function($event){_vm.dialogVisible=$event},"cancel":_vm.cancel},scopedSlots:_vm._u([{key:"title",fn:function(){return [_vm._v("\n    我是从插槽设置的标题\n    "),_c('el-button',{attrs:{"type":"warning","size":"mini"}},[_vm._v("头部操作的按钮")])]},proxy:true}])},[_vm._v(" "),_c('span',[_vm._v("自定义底部按钮")])])],1)}
var staticRenderFns = []

  
    const democomponentExport = {
    data() {
      return {
        dialogVisible: false,
      }
    },
    methods: {
      cancel() {
        this.dialogVisible = false
      }
    }
  };
    return {
      render,
      staticRenderFns,
      ...democomponentExport
    }
  })(),"element-demo2": (function() {
    
    var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',[_c('el-button',{attrs:{"type":"text"},on:{"click":function($event){_vm.dialogVisible = true}}},[_vm._v("点击打开 Dialog")]),_vm._v(" "),_c('apex-dialog',{attrs:{"title":"提示","visible":_vm.dialogVisible,"btn-list":_vm.btnList,"drag":false,"width":"30%"},on:{"update:visible":function($event){_vm.dialogVisible=$event},"confirm":_vm.confirm,"cancel":_vm.cancel}},[_c('span',[_vm._v("自定义底部按钮")])])],1)}
var staticRenderFns = []

  
    const democomponentExport = {
    data() {
      return {
        dialogVisible: false,
        btnList: [
          { text: '自定义按钮1', type: 'primary', emit: 'confirm' },
          { text: '自定义按钮2', type: 'default', emit: 'cancel' }
        ]
      }
    },
    methods: {
      confirm() {
        this.$message('点击了自定义按钮1')
      },
      cancel() {
        this.dialogVisible = false
      }
    }
  };
    return {
      render,
      staticRenderFns,
      ...democomponentExport
    }
  })(),"element-demo3": (function() {
    
    var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',[_c('el-button',{attrs:{"type":"text"},on:{"click":function($event){_vm.dialogVisible = true}}},[_vm._v("点击打开 Dialog")]),_vm._v(" "),_c('apex-dialog',{attrs:{"show-fullscreen":true,"title":"提示","visible":_vm.dialogVisible,"width":"30%"},on:{"update:visible":function($event){_vm.dialogVisible=$event},"toggleFullscreen":_vm.toggleFullscreenFn}},[_c('span',[_vm._v("这是一段信息")])])],1)}
var staticRenderFns = []

  
    const democomponentExport = {
    data() {
      return {
        dialogVisible: false
      };
    },
    methods: {
      toggleFullscreenFn(isFullscreen) {
        console.log(isFullscreen)
      }
    }
  };
    return {
      render,
      staticRenderFns,
      ...democomponentExport
    }
  })(),"element-demo4": (function() {
    
    var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',[_c('el-button',{attrs:{"type":"text"},on:{"click":function($event){_vm.dialogVisible = true}}},[_vm._v("点击打开 Dialog")]),_vm._v(" "),_c('apex-dialog',{attrs:{"drag":"","visible":_vm.dialogVisible,"width":"30%"},on:{"update:visible":function($event){_vm.dialogVisible=$event}}},[_c('span',[_vm._v("这是一段信息")])])],1)}
var staticRenderFns = []

  
    const democomponentExport = {
    data() {
      return {
        dialogVisible: false
      };
    }
  };
    return {
      render,
      staticRenderFns,
      ...democomponentExport
    }
  })(),"element-demo5": (function() {
    
    var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',[_c('el-button',{attrs:{"type":"text"},on:{"click":function($event){_vm.dialogVisible = true}}},[_vm._v("点击打开 Dialog")]),_vm._v(" "),_c('apex-dialog',{attrs:{"visible":_vm.dialogVisible,"url":_vm.url,"title":"外链嵌入","size":"medium","width":"70%","center":""},on:{"update:visible":function($event){_vm.dialogVisible=$event}}})],1)}
var staticRenderFns = []

  
    const democomponentExport = {
    data() {
      return {
        dialogVisible: false,
        url: 'https://www.hao123.com/'
      };
    }
  };
    return {
      render,
      staticRenderFns,
      ...democomponentExport
    }
  })(),"element-demo6": (function() {
    
    var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',[_c('el-button',{attrs:{"type":"text"},on:{"click":function($event){_vm.dialogTableVisible = true}}},[_vm._v("打开嵌套表格的 Dialog")]),_vm._v(" "),_c('apex-dialog',{attrs:{"title":"收货地址","visible":_vm.dialogTableVisible},on:{"update:visible":function($event){_vm.dialogTableVisible=$event}}},[_c('el-table',{attrs:{"data":_vm.gridData}},[_c('el-table-column',{attrs:{"property":"date","label":"日期","width":"150"}}),_vm._v(" "),_c('el-table-column',{attrs:{"property":"name","label":"姓名","width":"200"}}),_vm._v(" "),_c('el-table-column',{attrs:{"property":"address","label":"地址"}})],1)],1),_vm._v(" "),_c('el-button',{attrs:{"type":"text"},on:{"click":function($event){_vm.dialogFormVisible = true}}},[_vm._v("打开嵌套表单的 Dialog")]),_vm._v(" "),_c('apex-dialog',{attrs:{"title":"收货地址","visible":_vm.dialogFormVisible},on:{"update:visible":function($event){_vm.dialogFormVisible=$event}}},[_c('el-form',{attrs:{"model":_vm.form}},[_c('el-form-item',{attrs:{"label":"活动名称","label-width":_vm.formLabelWidth}},[_c('el-input',{attrs:{"autocomplete":"off"},model:{value:(_vm.form.name),callback:function ($$v) {_vm.$set(_vm.form, "name", $$v)},expression:"form.name"}})],1),_vm._v(" "),_c('el-form-item',{attrs:{"label":"活动区域","label-width":_vm.formLabelWidth}},[_c('el-select',{attrs:{"placeholder":"请选择活动区域"},model:{value:(_vm.form.region),callback:function ($$v) {_vm.$set(_vm.form, "region", $$v)},expression:"form.region"}},[_c('el-option',{attrs:{"label":"区域一","value":"shanghai"}}),_vm._v(" "),_c('el-option',{attrs:{"label":"区域二","value":"beijing"}})],1)],1)],1)],1)],1)}
var staticRenderFns = []

  
    const democomponentExport = {
    data() {
      return {
        gridData: [{
          date: '2016-05-02',
          name: '王小虎',
          address: '上海市普陀区金沙江路 1518 弄'
        }, {
          date: '2016-05-04',
          name: '王小虎',
          address: '上海市普陀区金沙江路 1518 弄'
        }, {
          date: '2016-05-01',
          name: '王小虎',
          address: '上海市普陀区金沙江路 1518 弄'
        }, {
          date: '2016-05-03',
          name: '王小虎',
          address: '上海市普陀区金沙江路 1518 弄'
        }],
        dialogTableVisible: false,
        dialogFormVisible: false,
        form: {
          name: '',
          region: '',
          date1: '',
          date2: '',
          delivery: false,
          type: [],
          resource: '',
          desc: ''
        },
        formLabelWidth: '120px'
      };
    }
  };
    return {
      render,
      staticRenderFns,
      ...democomponentExport
    }
  })(),"element-demo7": (function() {
    
    var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',[[_c('el-button',{attrs:{"type":"text"},on:{"click":function($event){_vm.outerVisible = true}}},[_vm._v("点击打开外层 Dialog")]),_vm._v(" "),_c('apex-dialog',{attrs:{"title":"外层 Dialog","show-footer":false,"visible":_vm.outerVisible},on:{"update:visible":function($event){_vm.outerVisible=$event}}},[_c('apex-dialog',{attrs:{"width":"30%","title":"内层 Dialog","visible":_vm.innerVisible,"append-to-body":""},on:{"update:visible":function($event){_vm.innerVisible=$event}}}),_vm._v(" "),_c('div',{staticClass:"dialog-footer"},[_c('el-button',{on:{"click":function($event){_vm.outerVisible = false}}},[_vm._v("取 消")]),_vm._v(" "),_c('el-button',{attrs:{"type":"primary"},on:{"click":function($event){_vm.innerVisible = true}}},[_vm._v("打开内层 Dialog")])],1)],1)]],2)}
var staticRenderFns = []

  
    const democomponentExport = {
    data() {
      return {
        outerVisible: false,
        innerVisible: false
      };
    }
  }
    return {
      render,
      staticRenderFns,
      ...democomponentExport
    }
  })(),"element-demo8": (function() {
    
    var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',[_c('el-button',{attrs:{"type":"text"},on:{"click":function($event){_vm.centerDialogVisible = true}}},[_vm._v("点击打开 Dialog")]),_vm._v(" "),_c('apex-dialog',{attrs:{"title":"提示","visible":_vm.centerDialogVisible,"width":"30%","center":""},on:{"update:visible":function($event){_vm.centerDialogVisible=$event}}},[_c('span',[_vm._v("需要注意的是内容是默认不居中的")])])],1)}
var staticRenderFns = []

  
    const democomponentExport = {
    data() {
      return {
        centerDialogVisible: false
      };
    }
  };
    return {
      render,
      staticRenderFns,
      ...democomponentExport
    }
  })(),
        }
      }
    </script>
  